import { useRef, useState } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

const triggerBlobDownload = (blob, filename) => {
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename || 'download'
	document.body.appendChild(link)
	link.click()
	link.remove()
	URL.revokeObjectURL(url)
}

export default function MyTrainingProofFilesSection({
	training,
	currentTrainer,
	onUploadTrainingFile,
	onDownloadTrainingFile,
	onDeleteTrainingFile,
}) {
	const { t } = useTranslation()
	const inputRef = useRef(null)
	const [saving, setSaving] = useState(false)
	const [uploading, setUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [uploadingFileName, setUploadingFileName] = useState('')
	const trainingId = training?._id || null
	const currentTrainerName = currentTrainer?.name || ''
	const canManageProofFiles = Boolean(currentTrainerName)
	const trainingFiles = Array.isArray(training?.trainingFiles) ? training.trainingFiles : []

	const handleUploadClick = () => {
		if (saving || uploading || !canManageProofFiles || !trainingId) return
		inputRef.current?.click()
	}

	const handleFilesChange = async event => {
		const files = Array.from(event.target.files || [])
		event.target.value = ''

		if (!files.length || !canManageProofFiles || !trainingId) return
		if (typeof onUploadTrainingFile !== 'function') {
			await Swal.fire(
				t('notifications.errorTitle'),
				t('myTrainings.details.proofFiles.messages.uploadFailed'),
				'error'
			)
			return
		}

		setUploading(true)
		setUploadProgress(0)
		let uploadedCount = 0

		for (let index = 0; index < files.length; index += 1) {
			const file = files[index]
			setUploadingFileName(file.name || '')

			const onUploadProgress = progressEvent => {
				const filePercent = progressEvent.total
					? Math.round((progressEvent.loaded * 100) / progressEvent.total)
					: 0
				const overallPercent = Math.round(((index * 100) + filePercent) / files.length)
				setUploadProgress(overallPercent)
			}

			const result = await onUploadTrainingFile(file, onUploadProgress)
			if (!result?.success) {
				setUploading(false)
				setUploadProgress(0)
				setUploadingFileName('')
				await Swal.fire(
					t('notifications.errorTitle'),
					result?.error || t('myTrainings.details.proofFiles.messages.uploadFailed'),
					'error'
				)
				return
			}
			uploadedCount += 1
			setUploadProgress(Math.round(((index + 1) * 100) / files.length))
		}

		setUploading(false)
		setUploadProgress(0)
		setUploadingFileName('')

		if (uploadedCount) {
			await Swal.fire(
				t('notifications.successTitle'),
				t('myTrainings.details.proofFiles.messages.uploadedSuccess'),
				'success'
			)
		}
	}

	const handleDownloadFile = async (fileUuid, fallbackFilename) => {
		if (!trainingId || !fileUuid || typeof onDownloadTrainingFile !== 'function') return

		setSaving(true)
		const result = await onDownloadTrainingFile(fileUuid)
		setSaving(false)

		if (!result?.success || !result.blob) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result?.error || t('myTrainings.details.proofFiles.messages.downloadFailed'),
				'error'
			)
			return
		}

		triggerBlobDownload(result.blob, result.filename || fallbackFilename)
	}

	const handleDeleteFile = async fileUuid => {
		if (!trainingId || !fileUuid || typeof onDeleteTrainingFile !== 'function') return

		const confirmation = await Swal.fire({
			title: t('myTrainings.details.proofFiles.deleteDialog.confirmTitle'),
			text: t('myTrainings.details.proofFiles.deleteDialog.confirmText'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('myTrainings.details.proofFiles.deleteDialog.confirmButton'),
			cancelButtonText: t('trainings.add.cancel'),
			confirmButtonColor: '#4f46e5',
		})

		if (!confirmation.isConfirmed) return

		setSaving(true)
		const result = await onDeleteTrainingFile(fileUuid)
		setSaving(false)

		if (!result?.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result?.error || t('myTrainings.details.proofFiles.messages.deleteFailed'),
				'error'
			)
			return
		}

		await Swal.fire(
			t('notifications.successTitle'),
			t('myTrainings.details.proofFiles.messages.deletedSuccess'),
			'success'
		)
	}

	return (
		<div className="mt-6 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h2 className="text-sm font-semibold text-gray-900">
						{t('myTrainings.details.sections.proofFiles')}
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						{t('myTrainings.details.proofFiles.description')}
					</p>
				</div>
				{canManageProofFiles ? (
					<>
						<button
							type="button"
							onClick={handleUploadClick}
							disabled={saving || uploading || !trainingId}
							className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-xs transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{uploading
								? t('myTrainings.details.proofFiles.actions.uploadingProgress', { progress: uploadProgress })
								: t('myTrainings.details.proofFiles.actions.upload')}
						</button>
						<input
							ref={inputRef}
							type="file"
							multiple
							className="hidden"
							disabled={uploading}
							onChange={handleFilesChange}
						/>
					</>
				) : null}
			</div>

			{uploading ? (
				<div className="mt-4">
					<div className="mb-1 flex justify-between text-xs text-gray-600">
						<span>
							{uploadingFileName
								? t('myTrainings.details.proofFiles.actions.uploadingFile', { fileName: uploadingFileName })
								: t('myTrainings.details.proofFiles.actions.uploading')}
						</span>
						<span>{uploadProgress}%</span>
					</div>
					<div className="h-2 w-full rounded-full bg-gray-200">
						<div
							className="h-2 rounded-full bg-indigo-600 transition-all duration-300 ease-out"
							style={{ width: `${uploadProgress}%` }}
						/>
					</div>
				</div>
			) : null}

			<div className="mt-4 space-y-3">
				{trainingFiles.length ? trainingFiles.map(file => (
					<div
						key={file.fileUuid}
						className="flex flex-col gap-3 bg-white p-4 sm:flex-row sm:items-stretch sm:justify-between"
					>
						<div className="flex min-w-0 flex-1 items-center">
							<p className="truncate text-sm font-semibold text-gray-900">
								{file.originalName || '—'}
							</p>
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => handleDownloadFile(file.fileUuid, file.originalName)}
								disabled={saving || uploading}
								className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{t('myTrainings.details.proofFiles.actions.open')}
							</button>
							{canManageProofFiles ? (
								<button
									type="button"
									onClick={() => handleDeleteFile(file.fileUuid)}
									disabled={saving || uploading}
									className="inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
								>
									{t('myTrainings.details.proofFiles.actions.delete')}
								</button>
							) : null}
						</div>
					</div>
				)) : (
					<div className="text-sm text-gray-500">
						{t('myTrainings.details.proofFiles.empty')}
					</div>
				)}
			</div>
		</div>
	)
}
